import { useEffect } from "react"
import {
  OrderByDir,
  RegistrationRoleEnum,
  ResourceFragment,
  useHomeRegistrationsQuery
} from "@pathwright/graphql-client/src/generated"
import { usePasteResource } from "../../../home/design/PasteResource"
import { usePathwrightContext } from "../../../pathwright/PathwrightContext"
import { toaster } from "@pathwright/ux/src/components/chakra/ui/toaster"
import Link from "@pathwright/ux/src/components/link/Link"
import { hasPathwrightStaffAccess } from "../../../user/permissions"
import { getPathUrl } from "../../../utils/urls"
import { useHomeRegistrationVars } from "../../registration/HomeRegistrationQueryVars"
import AddPathPromptCard from "./AddPathPromptCard"
import useOnPathCopied from "./useOnPathCopied"

function InnerCopyPathPromptCard({
  clip,
  onClear,
  mutation,
  loading,
  data
}: ReturnType<typeof usePasteResource>) {
  const clipResource = clip?.data?.resource as ResourceFragment | undefined
  const copiedResource = data?.copyResource

  // Consider submitting when mutation loading and the clip for the copied resource still exists.
  const isSubmitting = Boolean(loading || !!(copiedResource && clipResource))

  return (
    <AddPathPromptCard
      item={{
        pathName: clipResource?.name || clip?.object_label || "",
        pathBackground: clipResource
      }}
      isSubmitting={isSubmitting}
      onSubmit={async () => {
        await mutation()
      }}
      onCancel={onClear}
    />
  )
}

// For copying a path via clipboard by school members.
function MemberOnlyCopyPathPromptCard() {
  const pasteResource = usePasteResource({
    create_registration_with_role: RegistrationRoleEnum.Editor
  })
  const { positioningContext } = useHomeRegistrationVars()

  const copiedResource = pasteResource.data?.copyResource

  const registrationsQuery = useHomeRegistrationsQuery({
    variables: {
      first: 1,
      positioningContext: positioningContext!,
      orderBy: { created: OrderByDir.Desc }
    },
    skip: !copiedResource || !positioningContext,
    // Network-only to ensure we get the registration created from the copied resource,
    // and not from some previous cache state. But not "no-cache" so the registration
    // can be written to the cache for useOnPathCopied.
    fetchPolicy: "network-only"
  })

  // The registration most recently created (which should be the one created
  // from copying the resource). Ideally we'd query more directly, but we don't
  // have the GQL schema/query support for that yet.
  const copierRegistration =
    registrationsQuery.data?.me?.registrations?.edges?.[0]?.node || null

  // Passing onClear for cleanup to clear clipboard when done.
  useOnPathCopied(copierRegistration, pasteResource.onClear)

  return <InnerCopyPathPromptCard {...pasteResource} />
}

// For copying a path via clipboard by superusers.
// Currently, only school members can copy paths. This acts as a safeguard from any superusers being
// registered for the copied path (which also prevents them from becoming a school member).
function SuperuserCopyPathPromptCard() {
  const pasteResource = usePasteResource()
  const copiedResource = pasteResource.data?.copyResource

  // Clear the clipboard and show a toast when the path is copied.
  useEffect(() => {
    if (copiedResource) {
      pasteResource.onClear()

      toaster.create({
        type: "success",
        duration: 5000,
        closable: true,
        title: "Copied Path",
        description: (
          <>
            View{" "}
            {copiedResource.source_cohort?.id ? (
              <Link
                color="inherit"
                to={getPathUrl(
                  copiedResource.slug,
                  copiedResource.source_cohort.id
                )}
              >
                {copiedResource.name}
              </Link>
            ) : (
              copiedResource.name
            )}
          </>
        )
      })
    }
  }, [copiedResource])

  return <InnerCopyPathPromptCard {...pasteResource} />
}

// Wrapping in order to unmount the useMutation hook after clip successfully copied,
// so we can reset state if user copies again.
function CopyPathPromptCard() {
  const { enabled } = usePasteResource()
  const pwContext = usePathwrightContext()

  return enabled ? (
    pwContext.me.membership ? (
      <MemberOnlyCopyPathPromptCard />
    ) : hasPathwrightStaffAccess(pwContext) ? (
      <SuperuserCopyPathPromptCard />
    ) : null
  ) : null
}

export default CopyPathPromptCard
